import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Repeat2, Share2, Bookmark, MoreHorizontal, Trash2, Flag, Ban, VolumeX, Volume2, Link2, ChevronLeft, ChevronRight, Send, Play, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/ui/modal";
import { AuraRing } from "@/components/brand/AuraRing";
import { VerifiedBadge } from "@/components/brand/VerifiedBadge";
import { OgBadge } from "@/components/brand/OgBadge";
import { MentionText } from "@/components/common/MentionText";
import { MentionInput } from "@/components/common/MentionInput";
import { StoryComposer } from "@/components/app/Stories";
import { AudioPlayer } from "@/components/app/AudioPlayer";
import { ReportDialog, BlockConfirm } from "@/components/app/ReportDialog";
import { useAuth } from "@/context/AuthContext";
import { useAura } from "@/context/AuraContext";
import { api, mediaUrl, timeAgo, fmt, shareUrl, shareLink } from "@/lib/api";
import { cn } from "@/lib/utils";

function AudioInFeed({ m }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3">
      <AudioPlayer src={m.url} duration={m.duration} testid="post-audio" />
      {m.transcript && (
        <div className="mt-2">
          <button onClick={() => setOpen((o) => !o)} data-testid="post-transcript-toggle" className="text-xs font-semibold text-am-text2 hover:text-white">{open ? "Hide transcript" : "Show transcript"}</button>
          {open && <p data-testid="post-transcript" className="mt-1 rounded-xl border border-am-border bg-am-surface2/60 p-2.5 text-sm text-am-text2">{m.transcript}</p>}
        </div>
      )}
    </div>
  );
}

function MediaView({ media }) {
  const [i, setI] = useState(0);
  if (!media || media.length === 0) return null;
  if (media[0].type === "audio") return <AudioInFeed m={media[0]} />;
  const m = media[i];
  return (
    <div className="relative mt-3 overflow-hidden rounded-2xl border border-am-border bg-black">
      {m.type === "video" ? <VideoTile src={mediaUrl(m.url)} poster={m.poster ? mediaUrl(m.poster) : undefined} /> : <img src={mediaUrl(m.url)} alt="" className="max-h-[70vh] w-full object-contain" />}
      {media.length > 1 && (
        <>
          {i > 0 && <button onClick={() => setI(i - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 grid h-8 w-8 place-items-center rounded-full bg-black/50 text-white"><ChevronLeft className="h-4 w-4" /></button>}
          {i < media.length - 1 && <button onClick={() => setI(i + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 grid h-8 w-8 place-items-center rounded-full bg-black/50 text-white"><ChevronRight className="h-4 w-4" /></button>}
          <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1">{media.map((mm, k) => <span key={mm.url || k} className={cn("h-1.5 w-1.5 rounded-full", k === i ? "bg-white" : "bg-white/40")} />)}</div>
        </>
      )}
    </div>
  );
}

function VideoTile({ src, poster }) {
  const ref = useRef(null);
  const id = useRef(Math.random().toString(36).slice(2));
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [hasAudio, setHasAudio] = useState(true);

  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) el.play().then(() => setPlaying(true)).catch(() => {});
      else { el.pause(); setPlaying(false); el.muted = true; setMuted(true); }
    }, { threshold: 0.6 });
    obs.observe(el); return () => obs.disconnect();
  }, []);

  // Only one feed video plays audio at a time.
  useEffect(() => {
    const onOther = (e) => { if (e.detail !== id.current && ref.current) { ref.current.muted = true; setMuted(true); } };
    window.addEventListener("auramax-video-unmute", onOther);
    return () => window.removeEventListener("auramax-video-unmute", onOther);
  }, []);

  const toggleMute = (e) => {
    e.stopPropagation();
    const el = ref.current; if (!el) return;
    const nextMuted = !muted;
    el.muted = nextMuted;
    setMuted(nextMuted);
    if (!nextMuted) {
      window.dispatchEvent(new CustomEvent("auramax-video-unmute", { detail: id.current }));
      if (el.paused) el.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  const togglePlay = () => {
    const el = ref.current; if (!el) return;
    if (el.paused) { el.play(); setPlaying(true); } else { el.pause(); setPlaying(false); }
  };

  return (
    <div className="relative" onClick={togglePlay}>
      <video ref={ref} src={src} poster={poster} loop muted playsInline className="max-h-[70vh] w-full object-contain"
        onLoadedMetadata={(e) => { const t = e.target; setHasAudio(!(t.mozHasAudio === false || (t.webkitAudioDecodedByteCount !== undefined && t.webkitAudioDecodedByteCount === 0))); }} />
      {!playing && <div className="pointer-events-none absolute inset-0 grid place-items-center"><span className="grid h-14 w-14 place-items-center rounded-full bg-black/50 text-white"><Play className="h-6 w-6" /></span></div>}
      {hasAudio && (
        <button onClick={toggleMute} data-testid="video-mute-toggle" aria-label={muted ? "Unmute" : "Mute"}
          className="absolute bottom-3 right-3 grid h-9 w-9 place-items-center rounded-full bg-black/55 text-white backdrop-blur hover:bg-black/70">
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
}

function CommentRow({ c, small = false, onReply, canReply = false, postId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [liked, setLiked] = useState(c.liked);
  const [likes, setLikes] = useState(c.likes || 0);
  const [cmenu, setCmenu] = useState(false);
  const [report, setReport] = useState(false);
  const mine = user && c.author && user.user_id === c.author.user_id;
  const toggle = async () => {
    const nl = !liked; setLiked(nl); setLikes((n) => n + (nl ? 1 : -1));
    try { const r = nl ? await api.likeComment(c.id) : await api.unlikeComment(c.id); setLikes(r.likes); }
    catch (e) { setLiked(!nl); setLikes((n) => n + (nl ? -1 : 1)); toast(e.message); }
  };
  return (
    <div id={`comment-${c.id}`} data-testid={`comment-${c.id}`} className={cn("flex gap-2.5 p-1", small && "ml-9 mt-2")}>
      <button onClick={() => navigate(`/@${c.author.username}`)}><AuraRing src={mediaUrl(c.author.picture) || undefined} size={small ? 26 : 32} ring={1.5} fallback={c.author.name} /></button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 text-sm">
          <button onClick={() => navigate(`/@${c.author.username}`)} className="font-semibold text-white hover:underline">{c.author.name}</button>
          {c.author.is_verified && <VerifiedBadge size={12} />}{c.author.is_og && <OgBadge size={12} />}
          <span className="text-am-text3">· {timeAgo(c.created_at)}</span>
          {!mine && (
            <span className="relative ml-auto">
              <button onClick={() => setCmenu((m) => !m)} data-testid={`comment-more-${c.id}`} className="grid h-6 w-6 place-items-center rounded-full text-am-text3 hover:bg-white/5 hover:text-white"><MoreHorizontal className="h-4 w-4" /></button>
              {cmenu && (
                <span className="absolute right-0 top-7 z-30 block w-36 overflow-hidden rounded-xl border border-am-border bg-am-surface2 py-1 shadow-xl">
                  <button onClick={() => { setCmenu(false); setReport(true); }} data-testid={`comment-report-${c.id}`} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-am-text hover:bg-white/5"><Flag className="h-4 w-4" /> Report</button>
                </span>
              )}
            </span>
          )}
        </div>
        <MentionText text={c.text} className="block text-sm text-am-text" />
        <div className="mt-1 flex items-center gap-4 text-xs text-am-text3">
          <button onClick={toggle} data-testid={`comment-like-${c.id}`} className={cn("flex items-center gap-1 hover:text-white", liked && "text-red-500")}><Heart className={cn("h-3.5 w-3.5", liked && "fill-red-500")} /> {likes > 0 && fmt(likes)}</button>
          {canReply && <button onClick={() => onReply(c)} data-testid={`comment-reply-${c.id}`} className="font-semibold hover:text-white">Reply</button>}
        </div>
      </div>
      <ReportDialog open={report} onClose={() => setReport(false)} targetType="comment" targetId={c.id} label="comment" />
    </div>
  );
}

function CommentThread({ c, onReply }) {
  return (
    <div>
      <CommentRow c={c} onReply={onReply} canReply />
      {c.replies?.length > 0 && <div className="mt-1">{c.replies.map((r) => <CommentRow key={r.id} c={r} small />)}</div>}
    </div>
  );
}

export function PostCard({ post, onDeleted, focusCommentId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { spawnBurst } = useAura();
  const [liked, setLiked] = useState(post.liked);
  const [likes, setLikes] = useState(post.likes);
  const [reposted, setReposted] = useState(post.reposted);
  const [repostCount, setRepostCount] = useState(post.reposts || 0);
  const [saved, setSaved] = useState(post.saved);
  const [menu, setMenu] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState(null);
  const [ctext, setCtext] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [commentCount, setCommentCount] = useState(post.comments);
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [storyOpen, setStoryOpen] = useState(false);
  const inputRef = useRef(null);
  const a = post.author;
  const isSelf = user && a && user.user_id === a.user_id;

  const toggleLike = async () => {
    const nl = !liked; setLiked(nl); setLikes((n) => n + (nl ? 1 : -1));
    try { const r = nl ? await api.like(post.id) : await api.unlike(post.id); setLikes(r.likes); if (nl) spawnBurst(10); }
    catch (e) { setLiked(!nl); setLikes((n) => n + (nl ? -1 : 1)); toast(e.message); }
  };
  const toggleRepost = async () => {
    const nr = !reposted; setReposted(nr); setRepostCount((n) => n + (nr ? 1 : -1));
    try { const r = nr ? await api.repost(post.id) : await api.unrepost(post.id); setRepostCount(r.reposts); if (nr) { spawnBurst(15); toast("Reposted"); } }
    catch (e) { setReposted(!nr); setRepostCount((n) => n + (nr ? -1 : 1)); toast(e.message); }
  };
  const openComments = async () => { setCommentsOpen(true); try { setComments(await api.comments(post.id)); } catch { setComments([]); } };
  useEffect(() => {
    if (!focusCommentId) return;
    setCommentsOpen(true);
    api.comments(post.id).then(setComments).catch(() => setComments([]));
  }, [focusCommentId, post.id]);
  useEffect(() => {
    if (!focusCommentId || !comments) return;
    const el = document.getElementById(`comment-${focusCommentId}`);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-1", "ring-aura-cyan/60", "rounded-xl"); }
  }, [comments, focusCommentId]);
  const submitComment = async () => {
    const t = ctext.trim(); if (!t) return;
    setCtext(""); const parent = replyTo; setReplyTo(null);
    try {
      const c = await api.addComment(post.id, t, parent?.id);
      setComments((cur) => {
        if (!parent) return [c, ...(cur || [])];
        return (cur || []).map((x) => x.id === parent.id ? { ...x, replies: [...(x.replies || []), c] } : x);
      });
      setCommentCount((n) => n + 1); spawnBurst(20);
    } catch (e) { toast(e.message); }
  };
  const startReply = (c) => { setReplyTo(c); setCtext(`@${c.author.username} `); inputRef.current?.focus(); };
  const share = async () => {
    setShareOpen(false);
    const r = await shareLink(shareUrl(a.username), `${a.name} on AuraMax`);
    if (r === "copied") toast("Link copied"); else if (r === "failed") toast(shareUrl(a.username));
  };
  const doModerate = async (fn, msg) => { try { await fn(); toast(msg); setMenu(false); } catch (e) { toast(e.message); } };
  const isStaff = ["owner", "admin", "moderator"].includes(user?.role);
  const del = async () => { try { await (isSelf ? api.deletePost(post.id) : api.adminDeletePost(post.id)); toast(isSelf ? "Post deleted" : "Post removed"); onDeleted && onDeleted(post.id); } catch (e) { toast(e.message); } };

  if (hidden) return null;
  return (
    <article className="rounded-3xl border border-am-border bg-am-surface p-4" data-testid={`post-${post.id}`}>
      {post.reposted_by && (
        <button onClick={() => navigate(`/@${post.reposted_by.username}`)} className="mb-2 flex items-center gap-1.5 pl-1 text-xs font-semibold text-am-text2 hover:text-white">
          <Repeat2 className="h-3.5 w-3.5" /> {post.reposted_by.name} reposted
        </button>
      )}
      <div className="flex items-start gap-3">
        <button onClick={() => navigate(`/@${a.username}`)}><AuraRing src={mediaUrl(a.picture) || undefined} size={44} ring={2} fallback={a.name} /></button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <button onClick={() => navigate(`/@${a.username}`)} className="truncate font-semibold text-white hover:underline">{a.name}</button>
            {a.is_verified && <VerifiedBadge size={15} />}{a.is_og && <OgBadge size={15} />}
            <span className="truncate text-sm text-am-text2">@{a.username}</span>
            <span className="text-am-text3">·</span>
            <span className="text-xs text-am-text3">{timeAgo(post.created_at)}</span>
          </div>
          {a.show_category !== false && a.category && <div className="text-xs text-am-text3">{a.category}</div>}
        </div>
        <div className="relative">
          <button onClick={() => setMenu((m) => !m)} data-testid={`post-more-${post.id}`} className="grid h-8 w-8 place-items-center rounded-full text-am-text2 hover:bg-white/5"><MoreHorizontal className="h-5 w-5" /></button>
          {menu && (
            <div className="absolute right-0 top-9 z-30 w-48 overflow-hidden rounded-xl border border-am-border bg-am-surface2 py-1 shadow-xl">
              <button onClick={() => { setMenu(false); setStoryOpen(true); }} data-testid={`post-to-story-${post.id}`} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-am-text hover:bg-white/5"><PlusCircle className="h-4 w-4" /> Add to your Story</button>
              <button onClick={() => { share(); setMenu(false); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-am-text hover:bg-white/5"><Link2 className="h-4 w-4" /> Copy link</button>
              {isSelf ? (
                <button onClick={del} data-testid={`post-delete-${post.id}`} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-white/5"><Trash2 className="h-4 w-4" /> Delete</button>
              ) : (
                <>
                  {isStaff && <button onClick={del} data-testid={`post-mod-remove-${post.id}`} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-white/5"><Trash2 className="h-4 w-4" /> Remove (staff)</button>}
                  <button onClick={() => doModerate(() => api.mute(a.username), "Muted @" + a.username)} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-am-text hover:bg-white/5"><VolumeX className="h-4 w-4" /> Mute</button>
                  <button onClick={() => { setMenu(false); setBlockOpen(true); }} data-testid={`post-block-${post.id}`} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-am-text hover:bg-white/5"><Ban className="h-4 w-4" /> Block @{a.username}</button>
                  <button onClick={() => { setMenu(false); setReportOpen(true); }} data-testid={`post-report-${post.id}`} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-am-text hover:bg-white/5"><Flag className="h-4 w-4" /> Report</button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {post.text && <MentionText text={post.text} className="mt-2 block whitespace-pre-wrap text-[15px] leading-relaxed text-am-text" />}
      <MediaView media={post.media} />

      <div className="mt-3 flex items-center gap-5 text-am-text2">
        <button onClick={toggleLike} data-testid={`like-${post.id}`} className="flex items-center gap-1.5 text-sm transition-colors hover:text-white"><Heart className={cn("h-5 w-5", liked && "fill-red-500 text-red-500")} /> {fmt(likes)}</button>
        <button onClick={openComments} data-testid={`comment-${post.id}`} className="flex items-center gap-1.5 text-sm hover:text-white"><MessageCircle className="h-5 w-5" /> {fmt(commentCount)}</button>
        <button onClick={toggleRepost} data-testid={`repost-${post.id}`} className={cn("flex items-center gap-1.5 text-sm hover:text-white", reposted && "text-aura-mint")}><Repeat2 className="h-5 w-5" /> {repostCount > 0 && fmt(repostCount)}</button>
        <button onClick={() => setShareOpen(true)} data-testid={`share-${post.id}`} className="flex items-center gap-1.5 text-sm hover:text-white"><Share2 className="h-5 w-5" /></button>
        <button onClick={async () => { const ns = !saved; setSaved(ns); try { ns ? await api.savePost(post.id) : await api.unsavePost(post.id); } catch (e) { setSaved(!ns); toast(e.message); } }} data-testid={`save-${post.id}`} className="ml-auto hover:text-white"><Bookmark className={cn("h-5 w-5", saved && "fill-white text-white")} /></button>
      </div>

      <Modal open={commentsOpen} onClose={() => { setCommentsOpen(false); setReplyTo(null); setCtext(""); }} title="Comments" testid="comments-sheet">
        <div className="flex max-h-[70vh] flex-col">
          <div className="flex-1 space-y-4 overflow-y-auto p-4">
            {comments === null ? <p className="text-center text-sm text-am-text3">Loading…</p>
              : comments.length === 0 ? <p className="py-8 text-center text-sm text-am-text3">No comments yet. Be the first.</p>
              : comments.map((c) => <CommentThread key={c.id} c={c} onReply={startReply} />)}
          </div>
          {replyTo && (
            <div className="flex items-center justify-between border-t border-am-border px-3 py-1.5 text-xs text-am-text2">
              Replying to @{replyTo.author.username}
              <button onClick={() => { setReplyTo(null); setCtext(""); }} className="font-semibold text-white">Cancel</button>
            </div>
          )}
          <div className="flex items-center gap-2 border-t border-am-border p-3">
            <MentionInput inputRef={inputRef} value={ctext} onChange={(e) => setCtext(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitComment()} data-testid="comment-input" placeholder="Add a comment… use @ to mention" className="rounded-full border border-am-border bg-am-surface2 px-4 py-2.5 text-sm text-white outline-none focus:border-am-border-strong" />
            <button onClick={submitComment} data-testid="comment-submit" className="grid h-10 w-10 shrink-0 place-items-center rounded-full aura-gradient text-black"><Send className="h-4 w-4" /></button>
          </div>
        </div>
      </Modal>
      <Modal open={shareOpen} onClose={() => setShareOpen(false)} testid="share-sheet" className="max-w-md">
        <div className="p-5">
          <h3 className="mb-3 pr-8 font-display text-lg font-bold text-white">Share</h3>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => { setShareOpen(false); setStoryOpen(true); }} data-testid={`share-to-story-${post.id}`} className="flex items-center gap-3 rounded-xl border border-am-border bg-am-surface2 px-4 py-3 text-sm font-medium text-white hover:bg-white/5"><PlusCircle className="h-5 w-5 aura-text" /> Add to your Story</button>
            <button onClick={share} data-testid={`share-copy-${post.id}`} className="flex items-center gap-3 rounded-xl border border-am-border bg-am-surface2 px-4 py-3 text-sm font-medium text-white hover:bg-white/5"><Link2 className="h-5 w-5 text-am-text2" /> Copy link</button>
          </div>
        </div>
      </Modal>
      <StoryComposer open={storyOpen} onClose={() => setStoryOpen(false)} post={post} onPosted={() => window.dispatchEvent(new Event("story-posted"))} />
      <ReportDialog open={reportOpen} onClose={() => setReportOpen(false)} targetType="post" targetId={post.id} label="post" />
      <BlockConfirm open={blockOpen} onClose={() => setBlockOpen(false)} username={a.username} onBlocked={() => setHidden(true)} />
    </article>
  );
}
